// BrandedPortalPage — the org subdomain's front door ({slug}.stillwatersacademy.com/).
// PRIMARY AUDIENCE: the org admin moments after claiming their subdomain —
// this is the demo that sells the first seat. Secondary: manual URL typers and
// signed-out users (magic-link invites bypass it entirely). Zero DB queries —
// every number in the product mock is decorative. The org's accent is used on
// SURFACES (glows, borders, chips, button); all text stays white/muted with
// computed contrast (luminance check picks dark/white CTA text). No SWA
// pricing, doors, or upsells — ever — on an org's domain.
import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Sparkles, ShieldCheck, Flame, Trophy, CheckCircle2, Lock } from 'lucide-react'
import { useFacilityBranding, useOrgVocab } from '@/contexts/SubdomainBrandingContext'
import SEO from '@/components/layout/SEO'
import HomePage from './HomePage'

const M = '#020617'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.60)'
const MU2 = 'rgba(255,255,255,0.38)'
const BR = 'rgba(14,165,233,0.12)'
const serif = "'Playfair Display', Georgia, serif"

// ── Color math: contrast is computed, never guessed ──────────────────────────
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  const v = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)]
}
function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(c => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}
function shade(hex: string, pct: number): string {
  const [r, g, b] = hexToRgb(hex).map(c =>
    Math.max(0, Math.min(255, Math.round((c * (100 + pct)) / 100))))
  return `#${[r, g, b].map(c => c.toString(16).padStart(2, '0')).join('')}`
}

import BrandLogo from '@/components/common/BrandLogo'
import { useTranslation } from 'react-i18next';
import LanguagePicker from '@/components/university/ui/LanguagePicker'

// ── BrandMark — NO background/padding so square JPG logos don't bleed white
// corners. Uses BrandLogo to guarantee zero white bleed on any shape.
function BrandMark({ url, name, imgStyle, fallbackStyle }: {
  url: string | null
  name: string
  imgStyle: React.CSSProperties
  fallbackStyle: React.CSSProperties
}) {
  if (!url) return <span style={fallbackStyle}>{name}</span>
  return (
    <BrandLogo
      src={url}
      alt={name}
      style={{ objectFit: 'contain', display: 'block', ...imgStyle }}
      fallback={<span style={fallbackStyle}>{name}</span>}
    />
  )
}

const NOISE = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`

export default function BrandedPortalPage() {
  const { t } = useTranslation();

  const branding = useFacilityBranding()
  const vocab = useOrgVocab(branding.org_type)

  const accent    = branding.secondary_color || '#d4a843'
  const accentDk  = shade(accent, -35)
  const onAccent  = luminance(accent) > 0.35 ? '#020617' : '#ffffff'
  const hostname  = typeof window !== 'undefined' ? window.location.hostname : ''
  const ring = 78, circumference = 2 * Math.PI * (ring / 2 - 4)

  // ── Org-type-aware hero tagline: always uses display_name, never generic ──
  const heroTagline = (() => {
    const n = branding.display_name
    switch (branding.org_type) {
      case 'business':    return `${n}'s private training portal — AI-tutored ${vocab.coursePlural.toLowerCase()}, verified competency records, and credentials your clients can check in seconds.`
      case 'church':      return `${n}'s discipleship portal — Bible studies, guided reflection, and spiritual growth records your congregation can build on.`
      case 'school':      return `${n}'s learning hub — ${vocab.coursePlural.toLowerCase()} delivered by AI, mastery verified by the server, and printable evidence for every ${vocab.learnerSingular.toLowerCase()}.`
      case 'university':  return `${n}'s academic portal — ${vocab.coursePlural.toLowerCase()}, AI tutoring, and verified credentials your graduates carry into every interview.`
      case 'ngo':         return `${n}'s learning centre — ${vocab.coursePlural.toLowerCase()}, certified ${vocab.learnerPlural.toLowerCase()} records, and verifiable impact evidence.`
      case 'sports_club': return `${n}'s athlete portal — coaching programs, verified performance records, and credentials for every ${vocab.learnerSingular.toLowerCase()}.`
      default:            return `${n}'s learning portal — ${vocab.coursePlural.toLowerCase()}, a personal AI tutor, and mastery credentials anyone can verify.`
    }
  })()

  return (
    <div style={{ minHeight: '100dvh', background: M, color: TX, fontFamily: "'Inter', system-ui, sans-serif", display: 'flex', flexDirection: 'column' }}>
      <SEO title={`${branding.display_name} — ${vocab.learnerPortalName}`} noindex />

      {/* ── Ambient atmosphere: two org-colored glows + subtle grain ── */}
      <div aria-hidden style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.12, background: `radial-gradient(ellipse 55% 40% at 50% -5%, ${accent} 0%, transparent 65%)` }} />
        <div style={{ position: 'absolute', inset: 0, opacity: 0.08, background: `radial-gradient(ellipse 50% 40% at 85% 105%, #0369a1 0%, transparent 65%)` }} />
        <div style={{ position: 'absolute', inset: 0, opacity: 0.02, backgroundImage: NOISE, backgroundSize: 160 }} />
      </div>

      {/* ── Top bar: logo (if any) + ORG NAME left · engine credit right ── */}
      <header style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px clamp(12px,4vw,28px)', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {/* Logo is optional decoration; name is always the primary identity */}
          {branding.logo_url && (
            <BrandMark
              url={branding.logo_url}
              name=""
              imgStyle={{ height: 30, width: 'auto', maxWidth: 110, flexShrink: 0 }}
              fallbackStyle={{}}
            />
          )}
          <span style={{
            fontFamily: serif, fontWeight: 700, fontSize: 15, color: TX,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280,
          }}>
            {branding.display_name}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <LanguagePicker />
          <span style={{ fontSize: 10.5, color: MU2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.18em', flexShrink: 0 }}>
          Powered by Still Waters Academy
        </span>
        </div>
      </header>

      <main style={{ position: 'relative', zIndex: 1, flex: 1, maxWidth: 1080, width: '100%', margin: '0 auto', padding: 'clamp(16px,3vw,24px) clamp(12px,3vw,24px) 64px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 44 }}>

        {/* ── HERO: org name is ALWAYS the large headline; logo is NOT repeated ── */}
        <div style={{ textAlign: 'center', maxWidth: 640 }}>
          {/* Portal-type badge */}
          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 16px', borderRadius: 999, background: `${accent}1f`, border: `1px solid ${accent}55`, marginBottom: 26 }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: accent, boxShadow: `0 0 10px ${accent}` }} />
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: TX }}>
              {vocab.learnerPortalName} · Live
            </span>
          </motion.div>

          {/* ORG BUSINESS NAME — gradient headline, always shown */}
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.06 }}
            style={{
              fontFamily: serif,
              fontSize: 'clamp(2.4rem, 7vw, 3.8rem)',
              fontWeight: 700,
              lineHeight: 1.18,
              letterSpacing: '-0.02em',
              margin: '0 0 18px',
              background: `linear-gradient(135deg, #ffffff 30%, ${accent} 100%)`,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              display: 'inline-block',
              paddingBottom: '0.22em',
              paddingRight: '0.12em',
              paddingLeft: '0.04em',
              overflow: 'visible',
            }}
          >
            {branding.display_name}
          </motion.h1>

          {/* Org-type-aware tagline */}
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.18 }}
            style={{ fontSize: 16.5, lineHeight: 1.7, color: MU, margin: 0 }}
          >
            {heroTagline}
          </motion.p>
        </div>

        {/* ── PRODUCT MOCK — "you own this" moment. All decorative, zero DB. ── */}
        <motion.div
          initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.28 }}
          style={{ width: '100%', maxWidth: 720 }}
        >
          <div style={{
            borderRadius: 22, overflow: 'hidden', border: `1px solid ${BR}`,
            background: 'rgba(7,15,36,0.92)', backdropFilter: 'blur(14px)',
            boxShadow: `0 30px 80px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(14,165,233,0.15), 0 20px 60px -30px rgba(14,165,233,0.25)`,
          }}>
            {/* Browser chrome — their address, their site */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', borderBottom: `1px solid ${BR}`, background: 'rgba(255,255,255,0.03)' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {['#f87171', '#fbbf24', '#4ade80'].map(c => (
                  <span key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c, opacity: 0.65 }} />
                ))}
              </div>
              <div style={{ flex: 1, textAlign: 'center', fontSize: 11, color: MU2, fontFamily: 'monospace', background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '5px 10px', border: `1px solid ${BR}` }}>
                {'\uD83D\uDD12'} {hostname}
              </div>
            </div>

            {/* Mock learner dashboard */}
            <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Greeting */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800 }}>Welcome back, Alex</div>
                  <div style={{ fontSize: 11.5, color: MU2, marginTop: 2 }}>{t('whitelabel.keep_your_streak_alive')}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '5px 11px', borderRadius: 999, background: 'rgba(251,146,60,0.12)', color: '#fb923c', border: '1px solid rgba(251,146,60,0.3)' }}>
                    <Flame size={11} /> 12-day streak
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '5px 11px', borderRadius: 999, background: `${accent}16`, color: TX, border: `1px solid ${accent}44` }}>
                    <Trophy size={11} /> 3 mastered
                  </span>
                </div>
              </div>

              {/* Progress ring + bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 16, padding: '16px 18px' }}>
                <div style={{ position: 'relative', width: ring, height: ring, flexShrink: 0 }}>
                  <svg width={ring} height={ring}>
                    <circle cx={ring / 2} cy={ring / 2} r={ring / 2 - 4} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="5" />
                    <motion.circle
                      cx={ring / 2} cy={ring / 2} r={ring / 2 - 4} fill="none"
                      stroke={accent} strokeWidth="5" strokeLinecap="round"
                      strokeDasharray={circumference}
                      initial={{ strokeDashoffset: circumference }}
                      animate={{ strokeDashoffset: circumference * 0.13 }}
                      transition={{ duration: 1.4, delay: 0.7, ease: 'easeOut' }}
                      transform={`rotate(-90 ${ring / 2} ${ring / 2})`}
                    />
                  </svg>
                  <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 900 }}>87%</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {vocab.courseSingular} · Module 4
                  </div>
                  <div style={{ fontSize: 11, color: MU2, margin: '3px 0 9px' }}>{t('whitelabel.server_verified_mastery')}</div>
                  <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                    <motion.div
                      initial={{ width: 0 }} animate={{ width: '68%' }}
                      transition={{ duration: 1.2, delay: 0.9, ease: 'easeOut' }}
                      style={{ height: '100%', borderRadius: 3, background: `linear-gradient(90deg, ${accentDk}, ${accent})` }}
                    />
                  </div>
                </div>
              </div>

              {/* AI tutor + quiz evidence */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 240px', display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 14, padding: '11px 14px' }}>
                  <div style={{ width: 30, height: 30, borderRadius: 10, background: `${accent}1c`, border: `1px solid ${accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Sparkles size={14} color={accent} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>{t('whitelabel.your_ai_tutor')}</div>
                    <div style={{ fontSize: 10.5, color: MU2, marginTop: 1 }}>{t('whitelabel.ready_when_you_are')}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.28)', borderRadius: 14, padding: '11px 16px' }}>
                  <CheckCircle2 size={14} color="#4ade80" />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Quiz · 92%</div>
                    <div style={{ fontSize: 10.5, color: MU2, marginTop: 1 }}>{t('whitelabel.verified_evidence')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Feature tiles — vocab + org-name aware ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, width: '100%', maxWidth: 720 }}>
          {[
            {
              icon: <Sparkles size={15} />,
              title: '1:1 AI tutor',
              sub: `Teaches every ${vocab.courseSingular.toLowerCase()} concept-by-concept to every ${vocab.learnerSingular.toLowerCase()}`,
            },
            {
              icon: <ShieldCheck size={15} />,
              title: 'Verified credentials',
              sub: `${branding.display_name} credentials — verifiable by employers or inspectors in 30 seconds`,
            },
            {
              icon: <Trophy size={15} />,
              title: 'Real evidence',
              sub: `Every score, every retry, on the record — printable per ${vocab.learnerSingular.toLowerCase()}`,
            },
          ].map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.09 }}
              style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 18, padding: '20px 18px' }}
            >
              <div style={{ width: 34, height: 34, borderRadius: 11, background: `${accent}1c`, border: `1px solid ${accent}44`, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                {f.icon}
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>{f.title}</div>
              <div style={{ fontSize: 12, color: MU2, lineHeight: 1.5 }}>{f.sub}</div>
            </motion.div>
          ))}
        </div>

        {/* ── CTA & Access Governance — org name in button + pill ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.72 }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center' }}
        >
          <Link
            to="/auth/login"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10, padding: '17px 52px', borderRadius: 999,
              background: `linear-gradient(135deg, ${shade(accent, -18)}, ${accent})`,
              color: onAccent, fontWeight: 800, fontSize: 16.5, textDecoration: 'none',
              boxShadow: `0 10px 40px -8px ${accent}66`,
            }}
          >
            Access {branding.display_name} <ArrowRight size={17} />
          </Link>

          {/* Governance pill: uses org name, not generic "Organization" */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 18px', borderRadius: 999,
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
            fontSize: 12, color: MU2, fontWeight: 600, letterSpacing: '0.01em',
          }}>
            <Lock size={12} style={{ color: '#0ea5e9' }} />
            <span>Private Workspace · Access Provisioned by {branding.display_name}</span>
          </div>

          {/* Trust strip */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: 'rgba(255,255,255,0.28)', fontWeight: 500 }}>
            <span>{t('whitelabel.single_sign_on')}</span>
            <span>•</span>
            <span>{t('whitelabel.verifiable_credentials')}</span>
            <span>•</span>
            <span>256-bit Encryption</span>
          </div>
        </motion.div>
      </main>
    </div>
  )
}

// ─── Root router ──────────────────────────────────────────────────────────────
// Org subdomain → branded front door; main SWA domain → HomePage.
// Async: dark splash while branding loads, SWA fallback after 1.5 s.
export function RootPortalRouter() {
  const branding = useFacilityBranding()
  const [ready, setReady] = useState(false)
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const parts = hostname.split('.')
  const isLocalhostDev = parts.length === 2 && parts[1].startsWith('localhost')
  const isProdSubdomain = parts.length > 2 && parts[0] !== 'www'
  const hasSubdomain = (isLocalhostDev || isProdSubdomain) && parts[0] !== 'www'

  useEffect(() => {
    if (!hasSubdomain) return
    const t = setTimeout(() => setReady(true), 1500)
    return () => clearTimeout(t)
  }, [hasSubdomain])

  if (!hasSubdomain) return <HomePage />
  if (branding.is_branded) return <BrandedPortalPage />
  if (!ready) return <div style={{ minHeight: '100dvh', background: '#020617' }} />
  return <HomePage />
}